import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Avatar, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { fetchAuthorSuggestions } from '../../redux/features/authors/authorsSlice';
import { authorInitials } from '../../common/ItemTools';
import AuthorCandidateValue from './AuthorCandidateValue';
import { languageCodes } from '../../common/LanguageCodes';
import {
  BORDER, EDIT_FONT_SIZE, MUTED, SANS_STACK, SUCCESS, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';
import {
  GroupLabel, SelectInput, TextInput, autocompleteSx,
} from '../common/hero/styles';
import {
  withCreateOption, optionLabel, followableUrl,
} from '../common/hero/utils';
import { autocompletePopupSlotProps } from '../common/menu/primitives';
import useDragReorder from '../common/useDragReorder';
import SuggestionPanel, { SuggestionAgrees, SuggestionSkeleton } from '../common/hero/SuggestionPanel';
import { typeColor, typeNameMap, suggestionDisplay } from './utils';

// In batch mode this stands in for a language code and means "leave each
// item's language untouched".
const LANG_KEEP = '__keep';
const LANGUAGE_OPTIONS = languageCodes;

const urlRowSx = {
  boxSizing: 'border-box',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  border: `1.5px solid ${BORDER}`,
  borderRadius: '8px',
  padding: '0 11px',
  background: '#fff',
  '&:focus-within': {
    borderColor: SUCCESS,
    boxShadow: '0 0 0 4px rgba(76,175,80,0.12)',
  },
};

const bareUrlInputSx = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  padding: '7px 0',
  fontSize: EDIT_FONT_SIZE,
  fontFamily: SANS_STACK,
  color: TEXT_DARK,
  background: 'transparent',
};

const removeButtonSx = {
  border: 'none', background: 'none', color: MUTED, cursor: 'pointer', fontSize: 18, lineHeight: 1, p: '4px',
};

const AUTHOR_DND_TYPE = 'itemHeroAuthor';

// One draft author. Batch mode passes no `moveAuthor`, so its rows have no grip.
function DraftAuthorRow({
  author, index, batch, setAuthorName, removeAuthor, moveAuthor,
}) {
  const { rowRef, handleRef, isDragging } = useDragReorder(AUTHOR_DND_TYPE, index, moveAuthor);

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'flex', alignItems: 'center', gap: '8px', opacity: isDragging ? 0.45 : 1,
      }}
    >
      {moveAuthor && (
        <Box
          ref={handleRef}
          title="Drag to reorder"
          sx={{
            display: 'flex',
            flexShrink: 0,
            cursor: 'grab',
            color: '#c4c4c4',
            touchAction: 'none',
            '&:hover': { color: '#8a8a8a' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
      {author.imageUrl ? (
        <Avatar src={author.imageUrl} alt={author.name} sx={{ width: 30, height: 30, flexShrink: 0 }} />
      ) : (
        <Box sx={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: author.color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          flexShrink: 0,
        }}>
          {authorInitials(author.name)}
        </Box>
      )}
      {batch ? (
        <Box sx={{ flex: 1, fontSize: EDIT_FONT_SIZE, color: TEXT_DARK }}>{author.name}</Box>
      ) : (
        <TextInput
          value={author.name}
          onChange={(e) => setAuthorName(index, e.target.value)}
          placeholder="Author name"
          disabled={Boolean(author.authorId)}
          title={author.authorId ? 'Existing authors are edited from their author page' : ''}
          style={{ flex: 1, fontWeight: 500 }}
        />
      )}
      <Box component="button" onClick={() => removeAuthor(index)} sx={removeButtonSx}>
        ×
      </Box>
    </Box>
  );
}

DraftAuthorRow.propTypes = {
  author: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  batch: PropTypes.bool.isRequired,
  setAuthorName: PropTypes.func,
  removeAuthor: PropTypes.func.isRequired,
  moveAuthor: PropTypes.func,
};

DraftAuthorRow.defaultProps = {
  setAuthorName: undefined,
  moveAuthor: undefined,
};

/**
 * Shared edit form for the Type / Language / Read-time / Authors / Publishing /
 * Source-URLs block, used by both the single-item hero page and the batch-edit
 * panel.
 *
 * In `batch` mode the semantics change to keep/overwrite: Type and Language gain
 * a "keep current" option, authors are add-only chips (no inline rename or
 * reordering), and source URLs are a plain add/remove list (no
 * primary/visibility — a batch patch replaces the whole set). The single-item
 * behavior is unchanged when `batch` is false.
 */
export default function ItemHeroEditForm(props) {
  const {
    batch, namespace, draft, setField, toggleDateOnly, addAuthorEntry, setAuthorName, removeAuthor, moveAuthor,
    addUrl, setUrl, removeUrl, makeUrlPrimary, toggleUrlVisible,
    suggesting, candidatesFor, agreesOn, applied, applyCandidate, restoreField,
    authorCandidates, toggleAuthorCandidate,
  } = props;

  const dispatch = useDispatch();
  const coral = useTheme().palette.primary.main;
  const [authorOptions, setAuthorOptions] = useState([]);
  const [authorInput, setAuthorInput] = useState('');

  const selectionOnly = namespace === 'pending';
  const suggestNamespace = selectionOnly ? 'public' : 'private';

  const typeSet = !batch || draft.type !== '';
  const typeSelectColor = typeSet ? typeColor(Number(draft.type) || 0) : '#fff';

  // Single value fields.
  const metaPanel = (field) => (
    <>
      {suggesting && <SuggestionSkeleton height={34} />}
      <SuggestionPanel
        candidates={candidatesFor(field)}
        appliedId={applied[field]}
        onPick={(candidate) => applyCandidate(field, candidate)}
        onRestore={() => restoreField(field)}
        renderValue={(candidate) => (
          <Box sx={{ fontWeight: 600, fontSize: '.85rem', color: TEXT_DARK }}>
            {suggestionDisplay(field, candidate.value)}
          </Box>
        )}
        sx={{ mt: '8px', maxWidth: 246 }}
      />
    </>
  );

  const onAuthorInputChange = async (event, value) => {
    setAuthorInput(value);
    if (!value) { setAuthorOptions([]); return; }
    try {
      const suggestions = await dispatch(fetchAuthorSuggestions({
        namespace: suggestNamespace, searchTerm: value,
      })).unwrap();
      setAuthorOptions(suggestions || []);
    } catch (error) {
      setAuthorOptions([]);
    }
  };
  const onAuthorChange = (event, value) => {
    if (!value) return;
    // Selection-only: a free-typed name is ignored.
    if (typeof value === 'string' || value.inputValue) {
      if (selectionOnly) return;
      addAuthorEntry({ authorId: null, name: typeof value === 'string' ? value : value.inputValue });
    } else {
      addAuthorEntry({ authorId: value.authorId, name: value.name });
    }
    setAuthorInput('');
    setAuthorOptions([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', mb: '20px' }}>
        <Box>
          <GroupLabel>
            Type
            {agreesOn('type') && <SuggestionAgrees />}
          </GroupLabel>
          <SelectInput
            value={draft.type}
            onChange={(e) => setField('type', batch ? e.target.value : Number(e.target.value))}
            style={{
              minWidth: 150,
              background: typeSet ? typeSelectColor : '#fff',
              color: typeSet ? '#fff' : TEXT_DARK,
              fontWeight: 700,
              textTransform: 'lowercase',
              borderColor: typeSet ? typeSelectColor : BORDER,
            }}
          >
            {batch && <option value="" style={{ color: '#1a1a1a', background: '#fff' }}>keep current</option>}
            {Object.values(typeNameMap)
              .filter((t) => !batch || Number(t.value) !== 0)
              .map((t) => (
                <option key={t.value} value={t.value} style={{ color: '#1a1a1a', background: '#fff' }}>
                  {t.label.toLowerCase()}
                </option>
              ))}
          </SelectInput>
          {metaPanel('type')}
        </Box>
        <Box>
          <GroupLabel>
            Language
            {agreesOn('lang') && <SuggestionAgrees />}
          </GroupLabel>
          <SelectInput
            value={draft.lang}
            onChange={(e) => setField('lang', e.target.value)}
            style={{ minWidth: 150 }}
          >
            {batch
              ? <option value={LANG_KEEP}>keep current</option>
              : <option value="">—</option>}
            {LANGUAGE_OPTIONS.map((l) => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </SelectInput>
          {metaPanel('lang')}
        </Box>
        <Box>
          <GroupLabel>
            Read time
            {agreesOn('timeNeeded') && <SuggestionAgrees />}
          </GroupLabel>
          <TextInput
            value={draft.readTime}
            onChange={(e) => setField('readTime', e.target.value)}
            placeholder={batch ? 'keep — or e.g. 24 min' : 'e.g. 24 min'}
            style={{ minWidth: 150 }}
          />
          {metaPanel('timeNeeded')}
        </Box>
      </Box>

      <GroupLabel>{batch ? 'Set authors' : 'Authors'}</GroupLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {draft.authors.map((author, idx) => (
          <DraftAuthorRow
            // Draft authors have no id until saved, so index is the only stable key.
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            author={author}
            index={idx}
            batch={batch}
            setAuthorName={setAuthorName}
            removeAuthor={removeAuthor}
            moveAuthor={moveAuthor}
          />
        ))}
        <Autocomplete
          freeSolo={!selectionOnly}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          size="small"
          options={authorOptions}
          getOptionLabel={optionLabel}
          filterOptions={selectionOnly ? undefined : withCreateOption}
          isOptionEqualToValue={(option, value) => option.authorId === value.authorId}
          value={null}
          inputValue={authorInput}
          onInputChange={onAuthorInputChange}
          onChange={onAuthorChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={selectionOnly ? 'Add existing author' : `Add author${batch ? ', press Enter' : ''}`}
              sx={autocompleteSx}
            />
          )}
          slotProps={batch
            ? { ...autocompletePopupSlotProps, popper: { sx: { zIndex: 1600 } } }
            : autocompletePopupSlotProps}
          sx={{ width: '100%', mt: '4px' }}
        />
        {suggesting && <SuggestionSkeleton />}
        <SuggestionPanel
          candidates={authorCandidates}
          appliedLabel="Added"
          onPick={toggleAuthorCandidate}
          renderValue={(candidate) => <AuthorCandidateValue candidate={candidate} />}
          isApplied={(candidate) => draft.authors.some((a) => a.suggestId === candidate.id)}
          sx={{ mt: '8px' }}
        />
      </Box>

      <Box sx={{ mt: '22px' }}>
        <GroupLabel>Publishing</GroupLabel>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', alignItems: 'start',
        }}>
          <Box>
            <Box sx={{
              fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, mb: '5px',
            }}>
              Date published
              {agreesOn('timePublished') && <SuggestionAgrees />}
            </Box>
            <TextInput
              type={draft.dateOnly ? 'date' : 'datetime-local'}
              value={draft.datePublished}
              onChange={(e) => setField('datePublished', e.target.value)}
              style={{ width: '100%', padding: '7px 11px' }}
            />
            <Box
              component="button"
              onClick={toggleDateOnly}
              sx={{
                mt: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                fontSize: 12,
                fontWeight: 600,
                color: TEXT_SECONDARY,
                fontFamily: SANS_STACK,
              }}
            >
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '4px',
                border: draft.dateOnly ? `1px solid ${coral}` : '1.5px solid #b0b0b0',
                background: draft.dateOnly ? coral : '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {draft.dateOnly && <CheckIcon sx={{ fontSize: 11, color: '#fff' }} />}
              </Box>
              Date only
            </Box>
            {metaPanel('timePublished')}
          </Box>
          <Box>
            <Box sx={{
              fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, mb: '5px',
            }}>
              Date added
            </Box>
            <TextInput
              type="datetime-local"
              value={draft.dateCreated}
              onChange={(e) => setField('dateCreated', e.target.value)}
              style={{ width: '100%', padding: '7px 11px' }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: '22px' }}>
        <GroupLabel>{batch ? 'Set source URLs' : 'Source URLs'}</GroupLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {draft.urls.map((entry, idx) => (
            // Draft URLs have no id until saved, so index is the only stable key.
            // eslint-disable-next-line react/no-array-index-key
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!batch && (idx === 0 ? (
                <Box sx={{
                  background: coral,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}>
                  Primary
                </Box>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => makeUrlPrimary(idx)}
                  title="Make primary"
                  sx={{
                    background: '#fff',
                    color: TEXT_SECONDARY,
                    border: `1px solid ${BORDER}`,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 7px',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '.4px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    fontFamily: SANS_STACK,
                    '&:hover': { borderColor: coral, color: coral },
                  }}
                >
                  Make primary
                </Box>
              ))}
              <Box sx={urlRowSx}>
                <Box
                  component="input"
                  value={entry.url}
                  onChange={(e) => setUrl(idx, e.target.value)}
                  placeholder="https://..."
                  sx={bareUrlInputSx}
                />
                {followableUrl(entry.url) && (
                  <Box
                    component="a"
                    href={followableUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    sx={{
                      display: 'flex',
                      cursor: 'pointer',
                      color: '#8b8f96',
                      '&:hover': { color: coral },
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                  </Box>
                )}
              </Box>
              {!batch && (idx === 0 ? (
                <Box
                  title="The primary URL is always visible"
                  sx={{
                    display: 'flex', color: '#c4c7cc', p: '4px', flexShrink: 0,
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                </Box>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleUrlVisible(idx)}
                  title={entry.visible ? 'Shown — click to hide' : 'Hidden — click to show'}
                  sx={{
                    display: 'flex',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: entry.visible ? coral : MUTED,
                    p: '4px',
                    flexShrink: 0,
                  }}
                >
                  {entry.visible
                    ? <VisibilityIcon sx={{ fontSize: 18 }} />
                    : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                </Box>
              ))}
              {(batch || idx !== 0) && (
                <Box component="button" onClick={() => removeUrl(idx)} sx={removeButtonSx}>
                  ×
                </Box>
              )}
            </Box>
          ))}
          <Box
            component="button"
            onClick={addUrl}
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px dashed ${BORDER}`,
              borderRadius: '8px',
              padding: '5px 11px',
              fontSize: 11,
              fontWeight: 700,
              color: coral,
              cursor: 'pointer',
              background: '#fff',
              fontFamily: SANS_STACK,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <AddIcon sx={{ fontSize: 11 }} />
            Add URL
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

ItemHeroEditForm.propTypes = {
  batch: PropTypes.bool,
  namespace: PropTypes.string,
  draft: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired,
  toggleDateOnly: PropTypes.func.isRequired,
  addAuthorEntry: PropTypes.func.isRequired,
  setAuthorName: PropTypes.func,
  removeAuthor: PropTypes.func.isRequired,
  moveAuthor: PropTypes.func,
  addUrl: PropTypes.func.isRequired,
  setUrl: PropTypes.func.isRequired,
  removeUrl: PropTypes.func.isRequired,
  makeUrlPrimary: PropTypes.func,
  toggleUrlVisible: PropTypes.func,
  // Suggestions are not available in batch mode
  suggesting: PropTypes.bool,
  candidatesFor: PropTypes.func,
  agreesOn: PropTypes.func,
  applied: PropTypes.object,
  applyCandidate: PropTypes.func,
  restoreField: PropTypes.func,
  authorCandidates: PropTypes.array,
  toggleAuthorCandidate: PropTypes.func,
};

ItemHeroEditForm.defaultProps = {
  batch: false,
  namespace: 'private',
  setAuthorName: undefined,
  moveAuthor: undefined,
  makeUrlPrimary: undefined,
  toggleUrlVisible: undefined,
  suggesting: false,
  candidatesFor: () => [],
  agreesOn: () => false,
  applied: {},
  applyCandidate: () => {},
  restoreField: () => {},
  authorCandidates: [],
  toggleAuthorCandidate: () => {},
};
